/** The most records a list fetches and renders on one page. A larger size in a list's options is never offered. */
export const MAX_PAGE_SIZE = 50;

/**
 * The page sizes worth offering for `totalRecords`: only sizes up to `MAX_PAGE_SIZE` that do not exceed it (30 records
 * -> 10 and 25, not 50). With fewer records than the smallest size, the smallest stays. Original order is kept.
 */
export function visiblePageSizes<T extends number>(pageSizes: readonly T[], totalRecords: number): T[] {
  const allowed = pageSizes.filter((s) => s <= MAX_PAGE_SIZE);
  if (allowed.length === 0) return [];
  const smallest = Math.min(...allowed);
  return allowed.filter((s) => s <= totalRecords || s === smallest);
}

export interface ResolvedPageSize<T extends number> {
  /** The size the footer highlights. Always one of the sizes it offers. */
  shown: T;
  /** A size the list must switch to (and re-fetch with), or `null` when the one in use can stay. */
  request: T | null;
}

/**
 * Keeps the highlighted size among the offered ones when the size in use is above the record count:
 *   - every record already fits under the largest offered size (8 records at 25 -> 10; 25 at 50 -> 25): the footer
 *     just highlights that size. The page shows the same rows, so nothing is re-fetched.
 *   - it does not (30 records at 50 -> 25; 500 at 100 -> 50): the list switches to that size, so it pages by what is
 *     highlighted and never fetches more than `MAX_PAGE_SIZE`.
 * Nothing changes while a page loads or before the record count is known.
 */
export function resolvePageSize<T extends number>(
  pageSizes: readonly T[],
  totalRecords: number,
  pageSize: T,
  isFetching: boolean,
): ResolvedPageSize<T> {
  const keep = { shown: pageSize, request: null };
  if (isFetching || totalRecords <= 0) return keep;
  const visible = visiblePageSizes(pageSizes, totalRecords);
  if (visible.length === 0 || visible.includes(pageSize)) return keep;
  const largest = visible[visible.length - 1];
  return totalRecords <= largest ? { shown: largest, request: null } : { shown: largest, request: largest };
}
